import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { ResultsService } from "./results.service";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { SalesMailResult } from "./salesmail-result";
import { VideoDialogComponent } from "../video-dialog/video-dialog.component";
import { CorpClientsService } from "../corp-clients.service";
import { CorpClient } from "../shared-models/client.model";
import  moment from "moment";
import { ClipboardService } from "ngx-clipboard";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { filter } from "rxjs/operators";
import { MySalesMailResult } from "../my-results/my-results.service";
import {
  NewSalesMailResultMessage,
  NewSalesMailResult,
} from "../record/new-salesmail-result.model";
import { Router } from "@angular/router";
import { BrandService } from "../brands/brand.service";

@Component({
    selector: "app-results",
    templateUrl: "./results.component.html",
    styleUrls: ["./results.component.css"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ResultsComponent implements OnInit {
  isLoading: boolean = false;
  dataSource: MatTableDataSource<SalesMailResult> = new MatTableDataSource();
  tableColumns: string[] = [
    "cre_date",
    "salesmail",
    "video_name",
    "name",
    "short_url",
    "send",
    "shares",
    "views",
    "comments",
    "ctas",   
    "reactions",
  ];
  show_disabled = false;
  show_disabled_users = false;
  data: SalesMailResult[];
  currentClient: CorpClient;
  exportFilename: string;
  exportUrl: SafeResourceUrl;
  enableBrands: boolean = false;
  clientName: string = 'Default';
  skeletonRows = Array.from({ length: 5 }); // 5 fake rows
  
  readonly brandTableColumnName: string = 'brand_name';

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  constructor(
    private api: ResultsService,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    private corpService: CorpClientsService,
    private sanitizer: DomSanitizer,
    private clipboard: ClipboardService,
    private brandApi: BrandService
  ) {
    this.corpService.currentClient.subscribe((result) => {
      if (result == null) {
        return;
      }
      this.currentClient = result;
      this.client_first_load();
    });
  }

  ngOnInit() {
    this.dataSource = new MatTableDataSource();
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (
      data: SalesMailResult,
      sorthHeaderId: string
    ) => {
      switch (sorthHeaderId) {
        case "name":
          return String(data.first_name + " " + data.last_name).toLowerCase();
        case "video_name":
          return data.display_name || '';
        case "views":
          return data.Views;
        case "comments":
          return data.comment_count;
        case "ctas":
          return data.cta_count;
        case "reactions":
          return data.reaction_count;
        case "shares":
          return data.Shares;
        case "email":
          return String(data.email).toLowerCase();
        case "length":
          return data.video_length;
        case "cre_date":
          return data.cre_date.valueOf();
        case "salesmail":
          //Sort by len
          return data.video_length;
        case this.brandTableColumnName:
          return data.brand_name || 'Default';
        default:
          return "";
      }
    };
  }

  async client_first_load() {
    this.isLoading = true;
    await this.loadBrandSetting();
    await this.getResults();
  }

  async loadBrandSetting() {
    try {
      /* Get's the current users client. Note this may be different then the current client set from 
         the current client set from the observable if corp user
      */
      const client = await this.brandApi.getCurrentUserClient();

      // If the current user has brands enabled, show brand data.
      // Note that the api for results will included the brand data only if the user has access to it
      if(this.currentClient) {
        this.enableBrands = client.enable_brands;
        this.clientName = this.currentClient.client_name; // Show the current clietns name in case of no brand
        
        if(this.enableBrands && !this.tableColumns.includes(this.brandTableColumnName)) {
          this.tableColumns.splice(3, 0, this.brandTableColumnName); // Add it between salesmail thumbnail and send button
        }
      }
      
    } catch (error) {
      console.error(error);
      // on fail assume brand is disabled, don't show message
    }    
  }

  async getResults() {
    this.isLoading = true;
    this.data = [];
    this.dataSource.data = [];
    this.api
      .getResults(this.currentClient.client_id)
      .then((results) => {
        this.data = results.value;

        var filteredData = this.data; //All data

        if (!this.show_disabled) {
          //Filter out disabled videos if not checked
          filteredData = filteredData.filter(
            (result) => result.disabled == false
          );
        }

        if (!this.show_disabled_users) {
          //Filter out disabled users results if not checked
          filteredData = filteredData.filter(
            (result) => result.user_disabled == false
          );
        }

        this.dataSource.data = filteredData;
      })
      .catch((error) => {
        console.log(error);
        this.showErrorMessageSnackBar(
          "Could not load SalesMail results at this time."
        );
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  exportCsv() {
    let datetime = moment().format("YYYY-MM-DD_HH-mm");
    let filename = `${this.currentClient.client_name}-Results-${datetime}.csv`;
    let newline = "\r\n";
    let header = `"created_date","created_time","first_name","last_name","email","deleted","salesmail_url","video_length","shares","views","comments","reactions","ctas"${newline}`;
    let csv = header;

    this.data.map((e) => {
      let exportDate = moment(e.cre_date).local().format("YYYY-MM-DD");
      let exportTime = moment(e.cre_date).local().format("h:mm:ss a");
      let exportDisabled = e.disabled ? "True" : "False";
      let row = `"${exportDate}","${exportTime}","${e.first_name}","${e.last_name}","${e.email}","${exportDisabled}","${e.short_url}","${e.video_length}","${e.Shares}","${e.Views}","${e.comment_count}","${e.reaction_count}","${e.cta_count}"${newline}`;
      csv += row;
    });

    var blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    this.exportUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      URL.createObjectURL(blob)
    );
    this.exportFilename = filename;
  }

  hideDisabledClick() {
    var filteredData = this.data;

    if (!this.show_disabled) {
      //Filter out disabled videos if not checked
      filteredData = filteredData.filter((result) => result.disabled == false);
    }

    if (!this.show_disabled_users) {
      //Filter out disabled users results if not checked
      filteredData = filteredData.filter(
        (result) => result.user_disabled == false
      );
    }

    this.dataSource.data = filteredData;
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  showMessageSnackBar(message: string) {
    this.snackbar.open(message, "Close", {
      duration: 5000,
      panelClass: "message-snackbar",
    });
  }

  showErrorMessageSnackBar(message: string) {
    this.snackbar.open(message, "Close", {
      duration: 5000,
      panelClass: "error-snackbar",
    });
  }

  showVideo(row: SalesMailResult) {
    console.debug(row);
    this.dialog.open(VideoDialogComponent, {
      data: {
        title: row?.display_name ??`SalesMail from ${row.first_name} ${row.last_name}`,
        video_id: row.id
      },
    });
  }

  public copyToClipboard(text: string) {
    if (this.clipboard.copyFromContent(text)) {
      this.showMessageSnackBar("Short URL successfully copied.");
    } else {
      this.showErrorMessageSnackBar("Short URL failed to copy.");
    }
  }

  public sendSalesMail(row: SalesMailResult) {
    let shareResultMessage: NewSalesMailResultMessage = {
      url: row.short_url,
      userData: null,
      insertResult: null,
      videoId: row.id,
      user_id: row.user_id,
    };
    let shareMessage: NewSalesMailResult = { message: shareResultMessage };

    this.router.navigate(["share"], { state: { shareData: shareMessage } });
  }
}
